import { useInView } from '@/hooks/useInView'

/**
 * The brand conviction, delivered as a single oversized typographic statement
 * with a word-by-word scroll reveal (docs/BRAND-CONTEXT.md — "the bike is
 * built around the rider").
 */

interface ManifestoWord {
  text: string
  gold?: boolean
}

const STATEMENT: ManifestoWord[] = [
  { text: 'The' },
  { text: 'industry' },
  { text: 'builds' },
  { text: 'bikes,' },
  { text: 'then' },
  { text: 'finds' },
  { text: 'riders' },
  { text: 'to' },
  { text: 'fit' },
  { text: 'them.' },
  { text: 'We' },
  { text: 'begin' },
  { text: 'with' },
  { text: 'the' },
  { text: 'rider', gold: true },
  { text: '—' },
  { text: 'and' },
  { text: 'build' },
  { text: 'the' },
  { text: 'machine' },
  { text: 'that' },
  { text: 'should' },
  { text: 'have' },
  { text: 'existed' },
  { text: 'all' },
  { text: 'along.', gold: true },
]

/** Finite per-word delay classes — indexed by word position, no inline styles. */
const WORD_DELAYS = [
  '[transition-delay:0ms]',
  '[transition-delay:40ms]',
  '[transition-delay:80ms]',
  '[transition-delay:120ms]',
  '[transition-delay:160ms]',
  '[transition-delay:200ms]',
  '[transition-delay:240ms]',
  '[transition-delay:280ms]',
  '[transition-delay:320ms]',
  '[transition-delay:360ms]',
  '[transition-delay:400ms]',
  '[transition-delay:440ms]',
  '[transition-delay:480ms]',
  '[transition-delay:520ms]',
  '[transition-delay:560ms]',
  '[transition-delay:600ms]',
  '[transition-delay:640ms]',
  '[transition-delay:680ms]',
  '[transition-delay:720ms]',
  '[transition-delay:760ms]',
  '[transition-delay:800ms]',
  '[transition-delay:840ms]',
  '[transition-delay:880ms]',
  '[transition-delay:920ms]',
  '[transition-delay:960ms]',
  '[transition-delay:1000ms]',
]

export function ManifestoSection() {
  const { ref, inView } = useInView<HTMLDivElement>(0.25, 'home-manifesto')

  return (
    <section className="relative bg-carbon-950 texture-noise overflow-hidden py-36 md:py-44">
      <span
        aria-hidden
        className="pointer-events-none absolute -left-8 top-1/2 -translate-y-1/2 font-display font-extrabold uppercase tracking-tight [font-stretch:125%] leading-none text-[30vh] text-transparent [-webkit-text-stroke:1px_rgba(158,158,150,0.07)] select-none [writing-mode:vertical-rl] rotate-180"
      >
        Built Around You
      </span>

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
        <p
          className={`text-gold-500 font-mono text-xs tracking-[0.3em] uppercase mb-10 transition-all duration-1000 ease-out-expo ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          The Conviction
        </p>

        <p className="font-display font-bold uppercase leading-[1.08] tracking-tight text-3xl md:text-5xl lg:text-6xl">
          {STATEMENT.map(({ text, gold }, i) => (
            <span
              key={`${text}-${i}`}
              className={`inline-block mr-[0.28em] transition-all duration-700 ease-out-expo ${
                WORD_DELAYS[i] ?? WORD_DELAYS[WORD_DELAYS.length - 1]
              } ${inView ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-6 blur-sm'} ${
                gold
                  ? 'bg-gradient-gold bg-[length:200%_auto] bg-clip-text text-transparent animate-shimmer-slow motion-reduce:animate-none'
                  : 'text-carbon-50'
              }`}
            >
              {text}
            </span>
          ))}
        </p>

        <div
          className={`mt-14 flex items-center gap-6 transition-all duration-1000 ease-out-expo [transition-delay:1.1s] ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span aria-hidden className="w-16 h-px bg-gold-500/50" />
          <p className="font-mono text-2xs tracking-[0.3em] uppercase text-titanium-500">
            By commission only — never from stock
          </p>
        </div>
      </div>
    </section>
  )
}
